import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Section, Eyebrow, Heading, Lede } from "../components/site/Section";
import Placeholder from "../components/site/Placeholder";
import { NATIONS, ART } from "../lib/content";
import { getArchiveStats } from "../lib/archive";

const seasonOrder = ["Season One", "Season Two", "Season Three", "Later Seasons", "Untold"];

export default function NationsPage() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    (async () => {
      try { setStats(await getArchiveStats()); } catch { /* optional */ }
    })();
  }, []);

  const grouped = seasonOrder
    .map((s) => ({ season: s, items: NATIONS.filter((n) => n.season === s) }))
    .filter((g) => g.items.length);

  return (
    <div data-testid="nations-page">
      <Section testId="nations-hero" className="pt-12 md:pt-24">
        <Eyebrow>The Atlas</Eyebrow>
        <h1
          data-testid="nations-title"
          className="font-display text-4xl sm:text-5xl md:text-7xl text-[#EAE0D5] leading-[1] mt-4"
        >
          The Nine{" "}
          <span className="text-[#C5A059] italic font-light">Nations</span>
        </h1>
        <Lede>
          Across Bakhtak's nine nations, every culture wears a mask. Each one is
          buried beneath a unified identity engineered over 300 years of
          political choreography. The peace between them is not harmony — it is
          performance.
        </Lede>

        {/* Atlas banner — figure of the world */}
        <figure
          className="mt-12 md:mt-16 max-w-2xl mx-auto bg-[#0A0A0A] border border-[#C5A059]/20"
          data-testid="national-union-banner"
        >
          <Placeholder
            src={ART.nationsBanner}
            ratio="aspect-square"
            alt="A figure of Bakhtak"
            objectFit="contain"
          />
          <figcaption className="px-6 py-4 text-[10px] tracking-[0.28em] uppercase text-[#A39E99] border-t border-white/5 text-center">
            Somewhere in Bakhtak — between the chains and the river.
          </figcaption>
        </figure>
      </Section>

      {grouped.map((group) => (
        <Section
          key={group.season}
          testId={`nations-${group.season.toLowerCase().replace(/\s+/g, "-")}`}
          className="pt-0"
        >
          <div className="ornate-rule text-[10px] tracking-[0.32em] uppercase mb-10 md:mb-16 text-[#C5A059]">
            <span>{group.season}</span>
          </div>

          <div className="space-y-16 md:space-y-28">
            {group.items.map((n, i) => (
              <article
                key={n.slug}
                data-testid={`nation-${n.slug}`}
                className={`grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-center ${
                  i % 2 === 1 ? "md:[&>*:first-child]:order-2" : ""
                }`}
              >
                <div className="md:col-span-7">
                  <div
                    className="border"
                    style={{
                      borderColor: `${n.accent}40`,
                      boxShadow: `0 0 60px -25px ${n.accent}`,
                    }}
                  >
                    <Placeholder
                      src={n.image}
                      ratio="aspect-square"
                      label={n.image ? null : `${n.name} — visual TBA`}
                      alt={`${n.name}`}
                    />
                  </div>
                </div>

                <div className="md:col-span-5">
                  <div className="flex items-center gap-3 mb-2">
                    <span
                      className="text-[10px] tracking-[0.22em] uppercase px-2 py-1 border"
                      style={{ borderColor: `${n.accent}60`, color: n.accent }}
                      data-testid={`nation-${n.slug}-size`}
                    >
                      {n.size ? `Size #${n.size}` : n.sizeLabel || "—"}
                    </span>
                    <span
                      className="text-[10px] tracking-[0.22em] uppercase px-2 py-1 border"
                      style={{ borderColor: `${n.accent}60`, color: n.accent }}
                      data-testid={`nation-${n.slug}-pop`}
                    >
                      Pop #{n.population}
                    </span>
                    {stats && stats.nations[n.slug] && stats.nations[n.slug].total > 0 && (
                      <Link
                        to="/archive"
                        className="text-[10px] tracking-[0.22em] uppercase px-2 py-1 border inline-flex items-center gap-1.5 hover:bg-white/5"
                        style={{ borderColor: `${n.accent}60`, color: n.accent }}
                        data-testid={`nation-${n.slug}-archive`}
                        title="Open the Bakhtak Archive"
                      >
                        <span className="w-1 h-1 rounded-full" style={{ backgroundColor: n.accent }} />
                        Archive {stats.nations[n.slug].unlocked} / {stats.nations[n.slug].total}
                      </Link>
                    )}
                  </div>
                  <div
                    className="text-[10px] tracking-[0.32em] uppercase"
                    style={{ color: n.accent }}
                  >
                    {n.epithet}
                  </div>
                  <h2 className="font-display text-4xl md:text-5xl text-[#EAE0D5] mt-3">
                    {n.name}
                  </h2>
                  <p className="italic text-[#A39E99] mt-3 text-base">
                    "{n.motto}"
                  </p>

                  <dl className="mt-5 grid grid-cols-[max-content_1fr] gap-x-5 gap-y-2.5 text-xs">
                    <dt className="text-[#737373] tracking-[0.2em] uppercase">Virtue</dt>
                    <dd className="text-[#EAE0D5]">{n.slug === "zar" ? "????" : n.virtue}</dd>
                    <dt className="text-[#737373] tracking-[0.2em] uppercase">Flaw</dt>
                    <dd className="text-[#EAE0D5]">{n.slug === "zar" ? "????" : n.flaw}</dd>
                    <dt className="text-[#737373] tracking-[0.2em] uppercase">Power</dt>
                    <dd className="text-[#A39E99] leading-relaxed">{n.slug === "zar" ? "????" : n.power}</dd>
                    <dt className="text-[#737373] tracking-[0.2em] uppercase">Land</dt>
                    <dd className="text-[#A39E99] leading-relaxed">{n.land || n.aesthetic}</dd>
                    <dt className="text-[#737373] tracking-[0.2em] uppercase">Economy</dt>
                    <dd className="text-[#A39E99] leading-relaxed">{n.economy || "—"}</dd>
                  </dl>

                  <p className="text-[#A39E99] mt-5 text-base leading-relaxed">
                    {n.culture || n.summary}
                  </p>

                  <div className="flex flex-wrap gap-2 mt-6">
                    {n.tags.map((t) => (
                      <span
                        key={t}
                        className="text-[10px] tracking-[0.22em] uppercase px-3 py-1.5 border"
                        style={{
                          borderColor: `${n.accent}50`,
                          color: n.accent,
                        }}
                      >
                        {t}
                      </span>
                    ))}
                  </div>
                </div>
              </article>
            ))}
          </div>
        </Section>
      ))}
    </div>
  );
}
